/* eslint-disable @typescript-eslint/no-explicit-any */
import { gql } from 'apollo-boost';
import { useQuery } from 'react-apollo';
import { removeConnectionsMemoized } from '..';
import { useState, useEffect } from 'react';
import { Indicator } from 'npo/pages/ReportBuilder/Components/ImpactIndicator/utils';

const query = gql`
  query {
    allIndicators(filters: { version: "v4" }) {
      edges {
        node {
          dbId
          name
          definition
          guidance
          stageId
          customizable
          unit
          definitionStart
          definitionEnd
          serviceGoal
          initialForecastStart
          initialForecastEnd
          initialMeasurementStart
          initialMeasurementEnd
          finalForecastStart
          finalForecastEnd
          finalMeasurementStart
          finalMeasurementEnd
          impactReceiptText
          isHuman
          measurementTypeId
          measurementType {
            name
          }
          benchmarkType
          services {
            edges {
              node {
                dbId
                name
              }
            }
          }
          tags
        }
      }
    }
  }
`;

export const useAllIndicators = () => {
  const { data: rawData, refetch, loading, error } = useQuery(query, {
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });
  const data =
    rawData?.allIndicators?.edges.length > 0
      ? removeConnectionsMemoized(rawData?.allIndicators)
      : [];
  return { data, refetch, loading, error };
};

interface IndicatorByIdInternal {
  benchmarkType: null;
  customizable: true;
  dbId: number;
  definition: string;
  definitionEnd: string;
  definitionStart: string;
  finalForecastEnd: string;
  finalForecastStart: string;
  finalMeasurementEnd: string;
  finalMeasurementStart: string;
  guidance: string;
  impactReceiptText?: string | null;
  initialForecastEnd: string;
  initialForecastStart: string;
  initialMeasurementEnd: string;
  initialMeasurementStart: string;
  isHuman: false;
  measurementType: null;
  measurementTypeId: null;
  name: string;
  serviceGoal: string;
  services: {
    dbId: number;
    name: string;
  }[];
  stageId: number;
  tags: string[];
  unit: string;
}

export interface IndicatorById {
  [id: number]: IndicatorByIdInternal | undefined;
}

export const useIndicatorsById = () => {
  const i = useAllIndicators();
  if (i.loading || !i.data) {
    return { data: {}, isLoading: true };
  }
  const data: IndicatorByIdInternal[] = i.data;
  const result: IndicatorById = {};
  for (const d of data) {
    result[d.dbId] = d;
  }
  return { data: result || {}, isLoading: false };
};

export const queryMin = gql`
  query {
    allServices(filters: { isV4: true }) {
      edges {
        node {
          dbId
          name
          version
          indicators {
            edges {
              node {
                dbId
                name
                stageId
                customizable
                unit
                definitionStart
                definitionEnd
                serviceGoal
                initialForecastStart
                initialForecastEnd
                initialMeasurementStart
                initialMeasurementEnd
                finalForecastStart
                finalForecastEnd
                finalMeasurementStart
                finalMeasurementEnd
                isHuman
                definition
                stgName
                version
                impactReceiptText
              }
            }
          }
        }
      }
    }
  }
`;

export const useAllIndicatorsMin = () => {
  const { data: rawData, refetch, loading, error } = useQuery(queryMin, {
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });
  const data =
    rawData?.allServices?.edges.length > 0
      ? removeConnectionsMemoized(rawData?.allServices)
      : [];
  let sortedData = [];
  if (data) {
    sortedData = data
      .map(({ dbId: id, name, indicators, version }: any) => {
        const filteredIndicators = indicators.filter(
          (ind: any) => ind.version === 'v4',
        );

        if (filteredIndicators.length > 0) {
          return {
            id,
            name,
            version,
            indicators: filteredIndicators.map((ind: any) => ({
              name: ind.name,
              id: ind.dbId,
              stageId: ind.stageId,
              customizable: ind.customizable,
              definition: ind.definition,
              stgName: ind.stgName,
              version: ind.version,
              unit: ind.unit,
              definitionEnd: ind.definitionEnd,
              serviceGoal: ind.serviceGoal,
              definitionStart: ind.definitionStart,
              finalForecastEnd: ind.finalForecastEnd,
              isHuman: ind.isHuman,
              finalForecastStart: ind.finalForecastStart,
              finalMeasurementEnd: ind.finalMeasurementEnd,
              finalMeasurementStart: ind.finalMeasurementStart,
              initialForecastEnd: ind.initialForecastEnd,
              initialForecastStart: ind.initialForecastStart,
              initialMeasurementEnd: ind.initialMeasurementEnd,
              initialMeasurementStart: ind.initialMeasurementStart,
            })),
          };
        } else {
          return null; // Return null if there are no v4 indicators
        }
      })
      .filter((item: any) => item !== null && item.version === 'v4'); // Filter out null values
  }
  return { data: sortedData, refetch, loading };
};
